import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Play } from "lucide-react";
import { isSessionActive, subscribeSessionActive } from "@/lib/sessionHost";
import AppShell from "@/components/AppShell";

/**
 * HOME — the emotional center of Continua.
 *
 * A single large circular Play button continues the user's journey using
 * whatever they had last (companion / mode / duration / atmosphere). There is
 * NO configuration screen — Play just continues the relationship. A small
 * secondary button below opens the Characters list.
 *
 * AUTO-RESUME: if the user's most recent session is still inside its own
 * duration window (i.e. tonight IS still happening, not a memory), Play
 * lands directly on that session's player rather than starting a new one.
 * A fresh session only generates once tonight has drifted past the window.
 */
export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resumable, setResumable] = useState(null); // {id, remainingSec, atmoName}
  // Iter 86 — GLOBAL PLAYBACK: when a hosted session is running, Play simply
  // returns to it (the session view reattaches to the live audio).
  const [liveSession, setLiveSession] = useState(isSessionActive());
  useEffect(() => subscribeSessionActive(setLiveSession), []);
  // Iter 89 — the "still with you" line names the ACTIVE companion, not Serine.
  const companionName =
    user?.character_id === "elizabeth" ? "Elizabeth"
    : user?.character_id === "seriny" ? "Serine"
    : "Your companion";

  // Look for a resumable in-flight session on mount.
  useEffect(() => {
    if (!user?.character_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/sessions");
        if (cancelled) return;
        const latest = (data || []).find((s) => s.kind === "evening");
        if (!latest) return;
        const created = new Date(latest.created_at).getTime();
        const durationMs = (latest.duration_minutes || 15) * 60 * 1000;
        const remainingMs = created + durationMs - Date.now();
        if (remainingMs > 30 * 1000) {
          // Still inside the session window (with a 30s floor so we don't
          // resume something about to end anyway). Enable auto-resume.
          setResumable({
            id: latest.id,
            remainingSec: Math.round(remainingMs / 1000),
            atmoName: latest.atmosphere_name || "the quiet place",
            atmosphereId: latest.atmosphere_id,
            characterId: latest.character_id,
          });
        }
      } catch (e) { /* silent — fresh session is the safe fallback */ }
    })();
    return () => { cancelled = true; };
  }, [user?.character_id]);

  const startSession = async () => {
    if (!user) return;
    if (liveSession) {
      nav("/waiting"); // reattach to the running session — never spawn a new one
      return;
    }
    if (!user.character_id) {
      // First-time post-onboarding: nudge them to pick a companion.
      nav("/characters");
      return;
    }
    // Iter 87 — AUTO-RESUME with NO intermediate screen: reopen the immersive
    // session view directly; WaitingRoom reattaches to the existing session.
    if (resumable) {
      nav("/waiting", {
        state: {
          launchId: Date.now(),
          resumeSessionId: resumable.id,
          mode: user.default_mode || "sleep",
          durationMinutes: Math.max(1, Math.ceil(resumable.remainingSec / 60)),
          atmosphereId: resumable.atmosphereId || user.atmosphere_id || "forest_campfire",
          atmoName: resumable.atmoName,
          characterId: resumable.characterId || user.character_id,
          generateMorning: false,
        },
      });
      return;
    }
    setLoading(true);
    nav("/waiting", {
      state: {
        launchId: Date.now(), // Iter 86 — a fresh id starts a NEW hosted session
        mode: user.default_mode || "sleep",
        durationMinutes: 15,
        atmosphereId: user.custom_atmosphere ? "custom" : (user.atmosphere_id || "forest_campfire"),
        atmoName: user.custom_atmosphere?.name || user.atmosphere_name || "the quiet place",
        characterId: user.character_id,
        generateMorning: !!user.morning_enabled,
        customAtmosphere: user.custom_atmosphere,
      },
    });
  };

  return (
    <AppShell>
      <div className="min-h-[calc(100vh-12rem)] flex flex-col items-center justify-center px-6 -mt-8">
        {/* Large circular Play button */}
        <button
          type="button"
          onClick={startSession}
          disabled={loading}
          data-testid={liveSession ? "home-return-to-session" : "home-play-button"}
          aria-label="Continue your journey"
          className="relative group"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-violet-300/10 blur-2xl group-hover:bg-violet-300/16 transition-colors"
          />
          <span
            className={`relative flex items-center justify-center w-44 h-44 sm:w-52 sm:h-52 rounded-full border border-violet-200/35 bg-violet-200/8 backdrop-blur-md transition-all ${
              loading ? "opacity-60" : "group-hover:border-violet-100/55 group-hover:bg-violet-200/14 group-active:scale-95"
            }`}
          >
            <Play className="w-12 h-12 sm:w-14 sm:h-14 text-violet-50 translate-x-1" strokeWidth={1.25} />
          </span>
        </button>

        <p
          data-testid="home-play-helper"
          className="mt-8 text-sm text-violet-300/55 max-w-xs text-center leading-relaxed"
        >
          {loading
            ? "Settling the world for you…"
            : liveSession
              ? `${companionName} is still with you — return to your session.`
              : resumable
              ? `Return to ${resumable.atmoName.toLowerCase()}.`
              : user?.character_id
                ? "Continue your journey."
                : "Choose a companion to begin."}
        </p>

        {/* Iter 86 — the character selection control moved into the Sleep
            configuration area (Rest page): Sleep → Character → Duration → Play. */}
      </div>
    </AppShell>
  );
}
